
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Phone, MapPin, Send } from 'lucide-react';
import ScrollReveal from '../ui/ScrollReveal';
import { submitToPabbly } from '@/utils/pabblyConnect';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleCheckboxChange = (checked: boolean) => {
    setFormData(prev => ({ ...prev, consent: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.consent) {
      alert("Please agree to the terms and privacy policy to proceed.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Pass the form name without specifying a webhook URL - it will use the home_page one
      await submitToPabbly(formData, 'home_page_contact_form');
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: '',
        consent: false
      });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section-padding bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16">
          <div>
            <ScrollReveal>
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Contact Us
              </span>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Let's Discuss Your Project
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <p className="text-astrem-gray-600 mb-8">
                Ready to elevate your digital presence? Contact us to discuss your project and discover how Astrem can help you achieve your goals.
              </p>
            </ScrollReveal>

            <div className="space-y-6 mb-8">
              <ScrollReveal delay={300}>
                <div className="flex items-start">
                  <div className="bg-astrem-gray-100 p-3 rounded-full mr-4">
                    <Phone className="h-5 w-5 text-astrem-black" />
                  </div>
                  <div>
                    <h3 className="font-medium text-astrem-black mb-1">Phone</h3>
                    <a href="tel:225-331-6114" className="text-astrem-gray-600 hover:text-astrem-black transition-colors">
                      225-331-6114
                    </a>
                  </div>
                </div>
              </ScrollReveal>
              
              <ScrollReveal delay={500}>
                <div className="flex items-start">
                  <div className="bg-astrem-gray-100 p-3 rounded-full mr-4">
                    <MapPin className="h-5 w-5 text-astrem-black" />
                  </div>
                  <div>
                    <h3 className="font-medium text-astrem-black mb-1">Location</h3>
                    <p className="text-astrem-gray-600">
                      Based in Baton Rouge, Louisiana<br />
                      Serving clients across the United States
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>

          <ScrollReveal delay={300}>
            <div className="bg-white rounded-xl shadow-md border border-astrem-gray-200 p-6 md:p-8">
              <h3 className="text-xl font-medium mb-6">Send Us a Message</h3>
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-astrem-gray-700 mb-1">
                      First Name
                    </label>
                    <Input 
                      id="firstName" 
                      name="firstName" 
                      placeholder="Jane" 
                      className="border-astrem-gray-200 focus:border-astrem-black focus:ring-0"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-astrem-gray-700 mb-1">
                      Last Name
                    </label>
                    <Input 
                      id="lastName" 
                      name="lastName" 
                      placeholder="Doe" 
                      className="border-astrem-gray-200 focus:border-astrem-black focus:ring-0"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-astrem-gray-700 mb-1">
                    Email Address
                  </label>
                  <Input 
                    id="email" 
                    name="email" 
                    type="email" 
                    placeholder="jane@example.com" 
                    className="border-astrem-gray-200 focus:border-astrem-black focus:ring-0"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-astrem-gray-700 mb-1">
                    Subject
                  </label>
                  <Input 
                    id="subject" 
                    name="subject" 
                    placeholder="What can we help you with?" 
                    className="border-astrem-gray-200 focus:border-astrem-black focus:ring-0"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-astrem-gray-700 mb-1">
                    Message
                  </label>
                  <Textarea 
                    id="message" 
                    name="message" 
                    rows={5} 
                    placeholder="Tell us about your project..." 
                    className="border-astrem-gray-200 focus:border-astrem-black focus:ring-0"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="flex items-start space-x-2">
                  <Checkbox 
                    id="consent" 
                    checked={formData.consent} 
                    onCheckedChange={handleCheckboxChange}
                    className="mt-1"
                  />
                  <label htmlFor="consent" className="text-sm text-astrem-gray-700">
                    <span dangerouslySetInnerHTML={{ __html: `By checking this box, you agree to Astrem's <a class="tp text-astrem-black hover:underline" href="/terms" target="_blank">Terms of Service</a> and <a class="tp text-astrem-black hover:underline" href="/privacy-policy" target="_blank">Privacy Policy</a>. You also consent to receive text messages from Astrem. Message frequency may vary but is generally kept to a minimum. Standard messages and data rates may apply. For HELP, text us at 225-331-6114 or email admin@astrem.co. To unsubscribe, send STOP.<br><br>` }} />
                  </label>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-astrem-black hover:bg-astrem-gray-800 text-white"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                  <Send className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
